import * as React from 'react';
import { head, isDefined, last, noop } from 'react-financial-charts';
import type { DrawingSharedState, HoverTextConfig, MoreProps } from '@/types/commontypes';
import { getXValue } from '@react-financial-charts/core/lib/utils/ChartDataUtil';
import { saveNodeType } from 'react-financial-charts';
import { HoverTextNearMouse, generateLine, Text } from '@react-financial-charts/interactive/lib/components/index';
import { solidColor } from '@/utils/rgbTorgba';
import { retracementsPropetyWithColor } from '@/utils/defaultColors';
import { InteractiveStraightLine, ChannelWithArea, ClickableCircle } from '.';
import { getNewXY } from '@/utils/utils';
import { fixedAppearance } from '@/utils/defaultShapePropertyValues';

export interface EachFibRetracementProps {
  readonly x1: number;
  readonly x2: number;
  readonly y1: number;
  readonly y2: number;
  readonly yDisplayFormat: (value: number) => string;
  readonly type: string;
  readonly selected: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly appearance: Record<string, any>;
  readonly interactive: boolean;
  readonly hoverText: HoverTextConfig;
  readonly index?: number;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag: (e: React.MouseEvent, uuid: string, moreProps: MoreProps) => void;
  readonly onSelect?: (e: React.MouseEvent, uuid: string, moreProps: MoreProps) => void;
  readonly onDragStart?: (uuid: string) => void;
  readonly onHover?: (uuid: string | null, moreProps?: MoreProps) => void;
  readonly uuid: string;
  readonly locked: boolean;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly sharedState: DrawingSharedState;
  readonly isAnyShapeEnabled?: boolean;
}

interface EachFibRetracementState {
  hover: boolean;
  dragging?: boolean;
}

export class EachFibRetracement extends React.Component<EachFibRetracementProps, EachFibRetracementState> {
  public static defaultProps = {
    yDisplayFormat: (d: number) => d.toFixed(2),
    interactive: true,
    appearance: {
      strokeStyle: '#000000',
      strokeWidth: 1,
      fontFamily: "-apple-system, system-ui, Roboto, 'Helvetica Neue', Ubuntu, sans-serif",
      fontSize: 10,
      fontFill: '#000000',
      ...fixedAppearance,
      nsEdgeFill: '#000000',
      strokeDasharray: 'solid',
      trendLineAppearance: {
        strokeStyle: '#000',
        strokeWidth: 1,
        strokeDasharray: 'Solid',
        edgeFill: '#000000',
      },
    },
    selected: false,
    onDrag: noop,
    hoverText: {
      enable: false,
    },
    locked: false,
  };

  private dragStart!: { x1: number; y1: number; x2: number; y2: number };

  private saveNodeType: <T>(type: string) => React.Ref<T>;
  private lineInteractive: string;

  public constructor(props: EachFibRetracementProps) {
    super(props);

    this.saveNodeType = saveNodeType.bind(this);
    this.lineInteractive = 'react-financial-charts-pointer-cursor';
    this.state = {
      hover: false,
    };
  }

  public render() {
    const { x1, x2, y1, y2 } = this.props;
    const { interactive, yDisplayFormat, appearance, locked, onDoubleClick } = this.props;
    const { strokeWidth, strokeDasharray, trendlineAppearance, backgroundOpacity } = appearance;
    const { fontFamily, fontSize } = appearance;
    const { edgeStroke, nsEdgeFill, edgeStrokeWidth, r } = appearance;
    const { hoverText, selected } = this.props;
    const { hover } = this.state;
    const { onDragComplete } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const levels = (appearance.retracementLevels ? appearance.retracementLevels : retracementsPropetyWithColor) as
      | FibLevel[]
      | undefined;
    const lines = helper({ x1, x2, y1, y2, levels });
    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;
    const { dragging } = this.state;
    if (dragging) {
      this.lineInteractive = 'react-financial-charts-grabbing-cursor';
    } else if (!dragging) {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    } else {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    }
    // type(BOUND/EXTEND) 분기 제거 — extendLeft/extendRight만으로 확장 제어.
    // LINE + extendLeft/extendRight 조합이 XLINE/RAY보다 표현력이 높다 (좌우 독립 제어 가능).
    const lineType = 'LINE' as const;
    const safeLines = lines ?? [];
    const dir = head(safeLines)?.y1 > last(safeLines)?.y1 ? 3 : -1.3;

    const lockedR = locked ? r - 2 : r;
    const lockedEdgeStorke = locked ? '#436fe9' : edgeStroke;
    const lockedStrokeWidth = locked ? 1 : edgeStrokeWidth;

    return (
      <g>
        <InteractiveStraightLine
          ref={this.saveNodeType('line')}
          selected={selected || hover}
          onHover={this.handleHover}
          onUnHover={this.handleHover}
          x1Value={x1}
          y1Value={y1}
          x2Value={x2}
          y2Value={y2}
          type="LINE"
          strokeStyle={trendlineAppearance.strokeStyle}
          strokeDasharray={trendlineAppearance.strokeDasharray}
          strokeWidth={trendlineAppearance.strokeWidth}
          edgeFill={trendlineAppearance.edgeFill || 'rgba(255, 255, 255, 1)'}
          onDragComplete={onDragComplete}
          onDoubleClick={onDoubleClick}
          onMouseDown={this.handleShapeMouseDown}
        />
        {safeLines.map((line: FibLine, j: number) => {
          const text = `${line.percent}(${yDisplayFormat(line.y)})`;

          const xyProvider = ({ xScale, chartConfig }: MoreProps) => {
            const { yScale } = chartConfig;
            const {
              x1: lineX1,
              y1: lineY1,
              x2: lineX2,
            } = generateLine({
              type: lineType,
              start: [line.x1, line.y],
              end: [line.x2, line.y],
              xScale,
              yScale,
            });
            const x = xScale(Math.min(lineX1, lineX2)) - 75;
            const y = yScale(lineY1) + dir * 4 + 9;
            return [x, y];
          };

          const hoverHandler: {
            onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
            onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
          } = interactive ? { onHover: this.handleHover, onUnHover: this.handleHover } : {};
          const nextY = safeLines[j + 1] ? safeLines[j + 1].y : line.y;
          return (
            <g key={j}>
              <ChannelWithArea
                strokeStyle={'rgba(0,0,0,0)'}
                strokeWidth={0}
                strokeDasharray="Solid"
                opacity={backgroundOpacity}
                fillStyle={line.color}
                startXY={[line.x1, line.y]}
                endXY={[line.x2, line.y]}
                dy={nextY - line.y}
                type={lineType}
                extendLeft={appearance.extendLeft}
                extendRight={appearance.extendRight}
                onMouseDown={this.handleShapeMouseDown}
                onDoubleClick={onDoubleClick}
              />

              <InteractiveStraightLine
                ref={this.saveNodeType(`line_${j}`)}
                selected={selected || hover}
                {...hoverHandler}
                type={lineType}
                x1Value={line.x1}
                y1Value={line.y}
                x2Value={line.x2}
                y2Value={line.y}
                strokeStyle={line.color}
                strokeWidth={strokeWidth}
                strokeDasharray={strokeDasharray}
                interactiveCursorClass={this.lineInteractive}
                onDragStart={this.handleLineDragStart}
                onDrag={this.handleLineMove}
                onDragComplete={this.handleDragComplete}
                extendLeft={appearance.extendLeft}
                extendRight={appearance.extendRight}
                onMouseDown={this.handleShapeMouseDown}
                onDoubleClick={onDoubleClick}
              />
              <Text
                selected={selected}
                xyProvider={xyProvider}
                fontFamily={fontFamily}
                fontSize={fontSize}
                fillStyle={solidColor(line.color)}
              >
                {text}
              </Text>
            </g>
          );
        })}

        {/* Move these OUTSIDE the map so they always render */}
        <ClickableCircle
          ref={this.saveNodeType('edge1')}
          show={selected || hover || locked}
          cx={x1}
          cy={y1}
          r={lockedR}
          fillStyle={nsEdgeFill}
          strokeStyle={lockedEdgeStorke}
          strokeWidth={lockedStrokeWidth}
          interactiveCursorClass="big-declined-arrow-cursor"
          onDrag={this.handleEdge1Drag}
          onDragComplete={onDragComplete}
          onDoubleClick={onDoubleClick}
        />

        <ClickableCircle
          ref={this.saveNodeType('edge2')}
          show={selected || hover || locked}
          cx={x2}
          cy={y2}
          r={lockedR}
          fillStyle={nsEdgeFill}
          strokeStyle={lockedEdgeStorke}
          strokeWidth={lockedStrokeWidth}
          interactiveCursorClass="big-declined-arrow-cursor"
          onDrag={this.handleEdge2Drag}
          onDragComplete={onDragComplete}
          onDoubleClick={onDoubleClick}
        />

        <HoverTextNearMouse
          show={hoverTextEnabled && hover}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    this.setState({
      dragging: false,
    });

    const { onDragComplete, locked } = this.props;
    if (onDragComplete === undefined || locked) {
      return;
    }

    onDragComplete(e, moreProps);
  };

  private readonly handleShapeMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onSelect, uuid, sharedState } = this.props;
    if (sharedState?.status === 'started') return;
    if (onSelect && moreProps?.hovering && isDefined(uuid)) {
      onSelect(e, uuid, moreProps);
    }
  };

  private readonly handleEdge2Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, x1, y1, onDragStart, locked } = this.props;

    if (locked) return;

    if (onDragStart && isDefined(uuid)) {
      onDragStart(uuid);
    }

    const [x2, y2new] = getNewXY(moreProps);
    if (moreProps?.hovering) {
      onDrag(e, uuid, { ...moreProps, x1, y1, x2, y2: y2new });
    }
  };

  private readonly handleEdge1Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, x2, y2, onDragStart, locked } = this.props;

    if (locked) return;

    if (onDragStart && isDefined(uuid)) {
      onDragStart(uuid);
    }
    const [x1, y1new] = getNewXY(moreProps);
    if (moreProps?.hovering) {
      onDrag(e, uuid, { ...moreProps, x1, y1: y1new, x2, y2 });
    }
  };

  private readonly handleLineNSResizeTop = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, x1, x2, y2 } = this.props;
    const [, y1] = getNewXY(moreProps);
    onDrag(e, uuid, { ...moreProps, x1, y1, x2, y2 });
  };

  private readonly handleLineNSResizeBottom = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, x1, y1, x2 } = this.props;
    const [, y2] = getNewXY(moreProps);
    onDrag(e, uuid, { ...moreProps, x1, y1, x2, y2 });
  };

  private readonly handleLineMove = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onDrag, uuid, locked } = this.props;
    if (locked) return;
    const { x1: x1Value, y1: y1Value, x2: x2Value, y2: y2Value } = this.dragStart;
    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
      mouseXY,
    } = moreProps;
    const startPos = moreProps.startPos!;
    const hovering = moreProps.hovering || false;
    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullData);
    const newY1Value = yScale.invert(y1 - dy);
    const newX2Value = getXValue(xScale, xAccessor, [x2 - dx, y2 - dy], fullData);
    const newY2Value = yScale.invert(y2 - dy);
    this.setState({ dragging: true });
    if (!hovering) return;
    onDrag(e, uuid, { ...moreProps, x1: newX1Value, y1: newY1Value, x2: newX2Value, y2: newY2Value });
  };

  private readonly handleLineDragStart = () => {
    const { x1, y1, x2, y2, uuid, onDragStart, locked } = this.props;
    if (locked) return;
    if (onDragStart && uuid) {
      onDragStart(uuid);
    }
    this.setState({ dragging: true });
    this.dragStart = {
      x1,
      y1,
      x2,
      y2,
    };
  };

  private readonly handleHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onHover, sharedState, isAnyShapeEnabled } = this.props;
    if (sharedState?.status === 'started' || isAnyShapeEnabled) return;
    const hovering = moreProps.hovering || false;

    if (this.state.hover !== hovering) {
      this.setState({ hover: hovering });

      if (onHover) {
        onHover(hovering ? (uuid ?? null) : null, moreProps);
      }
    }
  };
}

interface FibLevel {
  show: boolean;
  value: number;
  color: string;
}

interface FibLine {
  percent: number;
  x1: number;
  x2: number;
  y: number;
  color: string;
}

function helper({
  x1,
  y1,
  x2,
  y2,
  levels,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  levels: FibLevel[] | undefined;
}): FibLine[] | undefined {
  const dy = Math.abs(y2 - y1);
  const isUp = y2 < y1;
  if (levels) {
    return levels
      .filter((level: FibLevel) => level.show)
      .map((level: FibLevel) => {
        const percent = level.value;

        const y = isUp ? y2 + dy * percent : y2 - dy * percent;

        return {
          percent,
          x1,
          x2,
          y,
          color: level.color,
        };
      });
  }
  return undefined;
}
